import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom';
import Slider from 'react-slick';
import axiosInstance from '../utils/axiosInstance';
import ProductCard from './ProductCard';
import { useTranslation } from 'react-i18next';
import { useMoney } from '../stores/currenciesStore';

const TopSellingOne = () => {
    const [mostSoldProducts, setMostSoldProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { t } = useTranslation();
    const { money } = useMoney();

    useEffect(() => {
        const fetchMostSoldProducts = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await axiosInstance.get('/api/metrics/products/most-sold');
                // Handle different possible response structures
                const products = response.data.data || response.data || [];
                setMostSoldProducts(products);
            } catch (err) {
                console.error('Error fetching most sold products:', err);
                setError(err.message || 'Failed to fetch most sold products');
            } finally {
                setLoading(false);
            }
        };

        fetchMostSoldProducts();
    }, []);

    // Extract the first product and the rest for the slider
    const featuredProduct = mostSoldProducts.length > 0 ? mostSoldProducts[0] : null;
    const sliderProducts = mostSoldProducts.length > 1 ? mostSoldProducts.slice(1) : [];

    function SampleNextArrow(props) {
        const { className, onClick } = props;
        return (
            <button
                type="button" onClick={onClick}
                className={` ${className} slick-next slick-arrow flex-center rounded-circle border border-gray-100 hover-border-neutral-600 text-xl hover-bg-neutral-600 hover-text-white transition-1`}
            >
                <i className="ph ph-caret-right" />
            </button>
        );
    }
    function SamplePrevArrow(props) {
        const { className, onClick } = props;

        return (

            <button
                type="button"
                onClick={onClick}
                className={`${className} slick-prev slick-arrow flex-center rounded-circle border border-gray-100 hover-border-neutral-600 text-xl hover-bg-neutral-600 hover-text-white transition-1`}
            >
                <i className="ph ph-caret-left" />
            </button>
        );
    }
    const settings = {
        dots: false,
        arrows: true,
        infinite: sliderProducts.length > 4,
        speed: 1000,
        slidesToShow: 4,
        slidesToScroll: 1,
        initialSlide: 0,
        autoplay: sliderProducts.length > 0,
        nextArrow: <SampleNextArrow />,
        prevArrow: <SamplePrevArrow />,
        responsive: [
            {
                breakpoint: 1399,
                settings: {
                    slidesToShow: 4,
                },
            },
            {
                breakpoint: 1199,
                settings: {
                    slidesToShow: 3,
                },
            },
            {
                breakpoint: 575,
                settings: {
                    slidesToShow: 2,
                },
            },
        ],
    };
    return (
        <section className="top-selling-products tw-pt-8 xl:tw-pt-16">
            <div className="container container-lg">
                <div className="p-24 border border-gray-100 rounded-16">
                    <div className="mb-24 section-heading">
                        <div className="flex-wrap gap-8 flex-between">
                            <h5 className="mb-0">{t('home.top_selling_products')}</h5>
                        </div>
                    </div>
                    <div className="row g-12">
                        <div className="col-md-4">
                            {loading ? (
                                <div className="overflow-hidden text-center position-relative rounded-16 p-28 z-1">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">{t('common.loading')}</span>
                                    </div>
                                </div>
                            ) : error ? (
                                <div className="overflow-hidden text-center position-relative rounded-16 p-28 z-1">
                                    <div className="text-danger">{error}</div>
                                </div>
                            ) : featuredProduct ? (
                                <div className="overflow-hidden text-center position-relative rounded-16 p-28 z-1">
                                    <img
                                        src="assets/images/bg/deal-bg.png"
                                        alt=""
                                        className="position-absolute inset-block-start-0 inset-inline-start-0 z-n1 w-100 h-100"
                                    />
                                    <div className="py-xl-4">
                                        <h5 className="tw-mb-8 fw-semibold">{t('common.best_seller')}</h5>
                                        <div className='tw-grid tw-grid-cols-1 lg:tw-grid-cols-2'>
                                            <div className="d-md-block d-none">
                                                {featuredProduct.images && featuredProduct.images.length > 0 ? (
                                                    <img 
                                                        src={featuredProduct.images[0]} 
                                                        alt={featuredProduct.name} 
                                                        style={{ maxHeight: '180px', objectFit: 'contain' }}
                                                    />
                                                ) : featuredProduct.image ? (
                                                    <img 
                                                        src={featuredProduct.image} 
                                                        alt={featuredProduct.name} 
                                                        style={{ maxHeight: '180px', objectFit: 'contain' }}
                                                    />
                                                ) : (
                                                    <img src="assets/images/thumbs/deal-img.png" alt="" />
                                                )}
                                            </div>
                                            <div className="tw-flex tw-flex-col tw-gap-1">
                                                <h5 className="fw-semibold">{featuredProduct.name}</h5>
                                                <h6 className='tw-text-sm'>{featuredProduct.short_description}</h6>
                                                <div className="mb-4">
                                                    {featuredProduct.computed_price !== featuredProduct.price && (
                                                        <span className="text-gray-400 !tw-text-2xl fw-semibold text-decoration-line-through me-2">
                                                            {money(featuredProduct.price || 0)}
                                                        </span>
                                                    )}
                                                    <span className="text-heading tw-text-2xl fw-semibold">
                                                        {money(featuredProduct.computed_price || 0)}
                                                    </span>
                                                </div>
                                                <div className="mt-3 mb-3">
                                                    <Link
                                                        to={`/products/${featuredProduct.slug}`}
                                                        className="gap-8 px-24 py-16 btn text-heading border-neutral-600 hover-bg-neutral-600 hover-text-white flex-center d-inline-flex rounded-pill fw-medium"
                                                        tabIndex={0}
                                                    >
                                                        {t('common.shop_now')} <i className="text-xl ph ph-shopping-cart d-flex" />
                                                    </Link>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                </div>
                            ) : (
                                <div className="overflow-hidden text-center position-relative rounded-16 p-28 z-1">
                                    <div className="py-xl-4">
                                        <h6 className="mb-4 fw-semibold">{t('common.no_products_found')}</h6>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="col-md-8">
                            <div className="top-selling-product-slider arrow-style-two">
                                {loading ? (
                                    <div className="p-5 text-center">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">{t('common.loading')}</span>
                                        </div>
                                    </div>
                                ) : error ? (
                                    <div className="p-5 text-center text-danger">
                                        {error}
                                    </div>
                                ) : sliderProducts.length === 0 ? (
                                    <div className="p-5 text-center">
                                        {t('common.no_products_found')}
                                    </div>
                                ) : (
                                    <Slider {...settings}>
                                        {sliderProducts.map((product, index) => (
                                            <div key={product.id}>
                                                <ProductCard 
                                                    product={product} 
                                                />
                                            </div>
                                        ))}
                                    </Slider>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

    )
}

export default TopSellingOne